import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// International checkout via PayPal — mirrors the 3-tier logic used for Razorpay
// and upgrades the user server-side on a successful capture.
export default function PayPalCheckout({ plan, planLabel, currency, priceDisplay, label = "or pay with PayPal", primary = false }) {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [payDisplay, setPayDisplay] = useState("");
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    api.get("/paypal/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  const payCurrency = (cfg?.supported_currencies || []).includes(currency) ? currency : "USD";

  // Fetch the price in the ACTUAL PayPal charge currency (may differ from the page
  // currency when the page shows an unsupported currency like INR/AED → USD).
  useEffect(() => {
    if (!cfg?.enabled) return;
    api.get(`/pricing?currency=${payCurrency}`)
      .then((r) => setPayDisplay(r.data?.plans?.[plan]?.display || ""))
      .catch(() => setPayDisplay(""));
  }, [cfg, payCurrency, plan]);

  // Keys not set → hide entirely (nothing to offer).
  if (!cfg || (!cfg.enabled && cfg.reason === "not_configured")) return null;

  // Account restricted (PayPal KYC/resolution pending) or reactive failure →
  // show a quiet "temporarily unavailable" note instead of a dead button.
  if (!cfg.enabled || unavailable) {
    return (
      <p className="w-full mt-2 text-center text-xs text-stone-400"
         data-testid={`paypal-unavailable-${plan}`}>
        PayPal temporarily unavailable — please use Razorpay above.
      </p>
    );
  }

  const track = (kind) =>
    api.post("/events/paywall", { feature: "checkout_paypal", kind, plan }).catch(() => {});

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className={`w-full rounded-full border border-stone-200 bg-white py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors ${primary ? "mt-8 btn-primary text-white hover:opacity-90" : "mt-2"}`}
          data-testid={`paypal-open-${plan}`}
          onClick={() => { if (!user) { nav("/login"); return; } track("buy_click"); }}
        >
          {label}
        </button>
      </DialogTrigger>
      <DialogContent data-testid={`paypal-dialog-${plan}`}>
        <DialogHeader>
          <DialogTitle>Pay with PayPal — {planLabel}</DialogTitle>
          <DialogDescription>
            Secure international checkout. You'll be charged in {payCurrency}
            {payDisplay ? ` (${payDisplay})` : ""}.
          </DialogDescription>
        </DialogHeader>
        <div className="pt-2" data-testid={`paypal-buttons-${plan}`}>
          <PayPalScriptProvider
            options={{ clientId: cfg.client_id, currency: payCurrency, intent: "capture" }}
            key={payCurrency}
          >
            <PayPalButtons
              style={{ layout: "vertical", shape: "pill", color: "gold" }}
              createOrder={async () => {
                try {
                  const r = await api.post("/paypal/create-order", { plan, currency: payCurrency });
                  return r.data.order_id;
                } catch (e) {
                  if (e?.response?.status === 503) {
                    // Account restricted — hide PayPal for this session.
                    setOpen(false);
                    setUnavailable(true);
                    toast.error("PayPal is temporarily unavailable. Please use Razorpay.");
                  }
                  throw e;
                }
              }}
              onApprove={async (data) => {
                const t = toast.loading("Confirming payment…");
                try {
                  await api.post("/paypal/capture-order", { order_id: data.orderID });
                  track("payment_verified");
                  await checkAuth();
                  toast.success("Payment successful! ✨", { id: t });
                  setOpen(false);
                  setTimeout(() => nav("/dashboard"), 1200);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not confirm payment", { id: t });
                }
              }}
              onError={() => { track("payment_failed"); }}
              onCancel={() => track("checkout_dismissed")}
            />
          </PayPalScriptProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
}
